import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { SearchProductOutputItem, useSearchProducts } from '@/lib/api/products';
import { cn } from '@/lib/utils';
import { Package, Plus, X } from 'lucide-react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { SearchProductInput } from '../search-product-input';

interface ProductTrigger {
  productId: string | null;
  productName?: string; // Optional - may not be present when loaded from DB
  imageUrl?: string | null; // Optional - may not be present when loaded from DB
  type: 'product' | 'any';
}

interface ProductTriggerSelectorProps {
  storeId: string;
  triggers: ProductTrigger[];
  onChange: (triggers: ProductTrigger[]) => void;
  error?: string;
}

const getProductImage = (product: SearchProductOutputItem): string | null => {
  const defaultVariant = product.variants.find((v) => v.default);
  return defaultVariant?.imageUrl ?? product.variants[0]?.imageUrl ?? null;
};

/**
 * Component for selecting multiple trigger products
 * Used to define which products trigger the upsell when added to cart
 */
export const ProductTriggerSelector: React.FC<ProductTriggerSelectorProps> = ({
  storeId,
  triggers,
  onChange,
  error,
}) => {
  const { t } = useTranslation('upsells');
  const [showSearch, setShowSearch] = React.useState(false);
  const [enrichedTriggers, setEnrichedTriggers] = React.useState<ProductTrigger[]>(triggers);

  // Get unique product IDs that need enrichment (missing name or image)
  const productIdsNeedingEnrichment = triggers
    .filter((t) => !t.productName || t.imageUrl === undefined)
    .map((t) => t.productId);

  // Load product details for triggers that need enrichment
  const { data: searchData } = useSearchProducts({
    storeId,
    productId: productIdsNeedingEnrichment.length > 0 ? productIdsNeedingEnrichment[0] : null,
    searchValue: productIdsNeedingEnrichment.length > 1 ? productIdsNeedingEnrichment.join(',') : null,
  });

  // Enrich triggers with product details when search data is available
  React.useEffect(() => {
    if (searchData && searchData.length > 0 && productIdsNeedingEnrichment.length > 0) {
      const enriched = triggers.map((trigger) => {
        // If already has name and image, keep as is
        if (trigger.productName && trigger.imageUrl !== undefined) {
          return trigger;
        }

        // Find product data
        const productData = searchData.find((p) => p.id === trigger.productId);
        if (productData) {
          return {
            ...trigger,
            productName: productData.name,
            imageUrl: getProductImage(productData),
          };
        }

        return trigger;
      });

      setEnrichedTriggers(enriched);
      // Update parent with enriched data
      if (JSON.stringify(enriched) !== JSON.stringify(triggers)) {
        onChange(enriched);
      }
    } else {
      setEnrichedTriggers(triggers);
    }
  }, [searchData, triggers, productIdsNeedingEnrichment.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleProductSelect = (product: SearchProductOutputItem) => {
    // Check for duplicates
    if (triggers.some((t) => t.productId === product.id)) {
      return;
    }

    const newTrigger: ProductTrigger = {
      productId: product.id,
      productName: product.name,
      imageUrl: getProductImage(product),
      type: 'product',
    };

    onChange([...triggers, newTrigger]);
    setShowSearch(false);
  };

  const handleProductRemove = (productId: string | null) => {
    onChange(triggers.filter((t) => t.productId !== productId));
  };

  return (
    <div className="space-y-3">
      {/* Selected Products List */}
      {enrichedTriggers.length > 0 && (
        <div className="space-y-2">
          {enrichedTriggers.map((trigger) => (
            <div
              key={trigger.productId}
              className={cn('bg-card flex items-center gap-3 rounded-md border p-3')}
            >
              <div className="bg-muted flex h-12 w-12 flex-shrink-0 items-center justify-center overflow-hidden rounded">
                {trigger.imageUrl ? (
                  <img
                    src={trigger.imageUrl}
                    alt={trigger.productName || 'Product'}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <Package className="text-muted-foreground h-6 w-6" />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">
                  {trigger.productName || `Product ${trigger.productId}`}
                </p>
                <p className="text-muted-foreground text-xs">{t('form.productTrigger.triggersUpsell')}</p>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => handleProductRemove(trigger.productId)}
                className="h-8 w-8 flex-shrink-0 p-0 text-red-600 hover:bg-red-50 hover:text-red-700"
                title={t('form.productTrigger.remove')}
              >
                <X className="h-4 w-4" />
                <span className="sr-only">{t('form.productTrigger.remove')}</span>
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* Add Product Section */}
      {showSearch ? (
        <div className="space-y-2">
          <Label>{t('form.productTrigger.searchLabel')}</Label>
          <SearchProductInput storeId={storeId} onSelect={handleProductSelect} />
          <Button type="button" variant="outline" size="sm" onClick={() => setShowSearch(false)}>
            {t('actions.cancel')}
          </Button>
        </div>
      ) : (
        <Button type="button" variant="outline" onClick={() => setShowSearch(true)} className="w-full">
          <Plus className="mr-2 h-4 w-4" />
          {t('form.productTrigger.addButton')}
        </Button>
      )}

      {/* Error Message */}
      {error && <p className="text-xs leading-tight text-red-600">{error}</p>}

      {/* Empty State */}
      {enrichedTriggers.length === 0 && !showSearch && (
        <p className="text-muted-foreground py-4 text-center text-sm">
          {t('form.productTrigger.noTriggers')}
        </p>
      )}
    </div>
  );
};
